import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { api, downloadFile, errMsg } from "@/lib/api";

const TONE = { new: "text-emerald-700", update: "text-sky-700", duplicate: "text-red-700", error: "text-slate-700" };
const NOTES = {
  groups: ["One row per Asset Group (site). Even a single asset at a site needs a group.", "customer_code must exist in Customers Master. site_id = a Customer Master site (id or name); anything else is kept as a typed Site ID.",
    "Blank group_name = Customer - Location. equipment_type must be in Supporting lists (Cold Rooms, Blast freezer, TAMsys…). amc_contract / iot_installed: Yes or No.",
    "Existing asset_group_code: only the filled cells are updated; blanks keep current values. Blank code = new group with auto AG- code."],
  units: ["Upload Asset Groups first - every unit needs an existing asset_group_code.", "item_code links to the Item Master when found; unknown (historical) codes are kept with item_name_snapshot. asset_type must be in Supporting lists (Blast freezers, IDU, ODU, TAMsys…).",
    "One serial number = one unit (quantity 1). Duplicate serials are rejected. Blank warranty dates come from the group.",
    "Existing asset_code: only the filled cells are updated. Blank asset_code = new unit with auto AU- code."],
};

function Uploader({ kind }) {
  const { can } = useMe();
  const fileRef = useRef(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (commit) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose the .xlsx or .csv file");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/assets/import/${kind}?commit=${commit}`, fd);
      setRes(r.data);
      if (commit) toast.success(`${r.data.summary.new} created, ${r.data.summary.update} updated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ok = res ? res.summary.new + res.summary.update : 0;
  return (
    <div data-testid={`asset-import-${kind}`}>
      <Panel className="mb-4">
        <ol className="text-sm text-slate-700 list-decimal ml-5 space-y-1">{NOTES[kind].map((n) => <li key={n}>{n}</li>)}<li>Dates as YYYY-MM-DD. Check the file first - nothing is saved until you import.</li></ol>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/assets/import/${kind}/template`, `asset_${kind}_template.xlsx`)} data-testid={`asset-${kind}-template-btn`}><Download className="h-4 w-4 mr-1" />Download {kind === "groups" ? "Asset Group" : "Asset Unit"} template</Button>
          {can("assets.import") ? <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={() => setRes(null)} className="text-sm" data-testid={`asset-${kind}-file-input`} /> : <span className="text-xs text-slate-500">Importing needs the operations or admin role.</span>}
          {can("assets.import") && <Button size="sm" variant="outline" onClick={() => run(false)} disabled={busy} data-testid={`asset-${kind}-check-btn`}><FileSpreadsheet className="h-4 w-4 mr-1" />Check file</Button>}
          {can("assets.import") && res && !res.committed && <Button size="sm" onClick={() => run(true)} disabled={busy || !ok} data-testid={`asset-${kind}-commit-btn`}>Import {res.summary.new} new + {res.summary.update} updates</Button>}
        </div>
      </Panel>
      {res && (
        <Panel title={`${res.committed ? "Imported" : "Check"} — new ${res.summary.new} · update ${res.summary.update} · duplicate ${res.summary.duplicate} · error ${res.summary.error}`} testid={`asset-${kind}-result`}>
          <div className="max-h-96 overflow-auto"><table className="w-full text-xs">
            <thead><tr className="th text-left"><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Record</th><th className="px-2 py-1.5">Result</th><th className="px-2 py-1.5">Detail</th></tr></thead>
            <tbody>{res.rows.map((r) => <tr key={r.row} className="border-b border-slate-100"><td className="px-2 py-1.5 font-mono">{r.row}</td><td className="px-2 py-1.5 font-mono">{r.key}</td>
              <td className={`px-2 py-1.5 uppercase font-mono font-semibold ${TONE[r.status]}`}>{r.status}</td><td className="px-2 py-1.5 text-slate-600">{r.message}</td></tr>)}</tbody>
          </table></div>
        </Panel>
      )}
    </div>
  );
}

export default function AssetImport() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("type") === "units" ? "units" : "groups";
  return (
    <div data-testid="asset-import-page">
      <PageHeader title="Asset upload" subtitle="Separate uploads: Asset Groups (sites) first, then Asset Units (equipment) linked by asset_group_code.">
        <Button variant="outline" size="sm" asChild><Link to="/assets/groups">Asset Group Register</Link></Button>
      </PageHeader>
      {sp.get("group") && <p className="text-xs text-sky-800 mb-2" data-testid="asset-import-group-hint">Uploading units for <b className="font-mono">{sp.get("group")}</b> - put this code in the asset_group_code column.</p>}
      <Tabs value={tab} onValueChange={(v) => setSp({ type: v })}>
        <TabsList><TabsTrigger value="groups" data-testid="asset-import-tab-groups">A. Asset Groups</TabsTrigger><TabsTrigger value="units" data-testid="asset-import-tab-units">B. Asset Units</TabsTrigger></TabsList>
        <TabsContent value="groups"><Uploader kind="groups" /></TabsContent>
        <TabsContent value="units"><Uploader kind="units" /></TabsContent>
      </Tabs>
    </div>
  );
}
